import React, { useState, useEffect, useCallback } from 'react';

const slides = [
  {
    image: "https://images.unsplash.com/photo-1666214280557-f1b5022eb634?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80",
    animation: "scale",
    ariaLabel: "Doctor discussing patient education materials"
  },
  {
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80",
    animation: "scale",
    ariaLabel: "Patient receiving facial treatment at medical spa"
  },
  {
    image: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80",
    animation: "scale",
    ariaLabel: "Female Asian dentist consulting with patient"
  },
  {
    image: "https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80",
    animation: "scale",
    ariaLabel: "Healthcare professional reviewing digital patient information"
  },
  {
    image: "https://images.unsplash.com/photo-1581595219315-a187dd40c322?ixlib=rb-1.2.1&auto=format&fit=crop&w=2070&q=80",
    animation: "scale",
    ariaLabel: "Medical team collaborating on patient care"
  }
];

interface HeroCarouselProps {
  children: React.ReactNode;
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ children }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [autoplayPaused, setAutoplayPaused] = useState(false);

  const transition = useCallback((newIndex: number) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(newIndex);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 1000);
  }, [isTransitioning]);

  useEffect(() => {
    if (autoplayPaused) return;

    const timer = setInterval(() => {
      if (!isTransitioning) {
        const nextIndex = (currentSlide + 1) % slides.length;
        transition(nextIndex);
      }
    }, 8000);

    return () => clearInterval(timer);
  }, [currentSlide, transition, isTransitioning, autoplayPaused]);

  const handleMouseEnter = () => setAutoplayPaused(true);
  const handleMouseLeave = () => setAutoplayPaused(false);

  return (
    <div 
      className="relative min-h-[100svh] overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="region"
      aria-label="Hero image carousel"
    >
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/60 to-indigo-600/60 mix-blend-multiply z-10" />

      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={index !== currentSlide}
        >
          <img
            src={slide.image}
            alt={slide.ariaLabel}
            className={`w-full h-full object-cover object-center ${
              index === currentSlide
                ? slide.animation === 'scale'
                  ? 'animate-ken-burns'
                  : 'animate-soft-zoom'
                : ''
            }`}
            loading={index === 0 ? "eager" : "lazy"}
            fetchpriority={index === 0 ? "high" : "auto"}
          />
        </div>
      ))}

      {/* Slide indicators */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => transition(index)}
            disabled={isTransitioning}
            className={`h-1.5 sm:h-2 rounded-full transition-all duration-500 shadow-lg ${
              index === currentSlide 
                ? 'bg-white w-6 sm:w-8 scale-110' 
                : 'bg-white/50 hover:bg-white/75 w-1.5 sm:w-2 hover:scale-110'
            }`}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentSlide}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-30 h-full">
        {children}
      </div>
    </div>
  );
};

export default HeroCarousel;